import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquare, Reply } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const when = (iso) => new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" });

function ReplyBox({ patientId, m, onDone }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const send = async () => {
    try { await api.post(`/patients/${patientId}/team-messages/${m.id}/reply`, { text }); toast.success("Réponse envoyée à la famille"); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Envoi impossible."); }
  };
  if (!open) return <Button variant="outline" size="sm" className="rounded-lg mt-3" onClick={() => setOpen(true)} data-testid={`team-reply-open-${m.id}`}><Reply className="w-4 h-4 mr-1" /> Répondre (facultatif)</Button>;
  return (
    <div className="mt-3 space-y-2">
      <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} maxLength={1000} placeholder="Votre réponse à la famille…" data-testid={`team-reply-input-${m.id}`} />
      <Button size="sm" disabled={!text.trim()} onClick={send} className="rounded-lg bg-emerald-700 hover:bg-emerald-800" data-testid={`team-reply-send-${m.id}`}>Envoyer la réponse</Button>
    </div>
  );
}

// Messages des familles reçus pour ce résident.
export default function TeamMessagesCard({ patient }) {
  const [msgs, setMsgs] = useState([]);
  const load = () => api.get(`/patients/${patient.id}/team-messages`).then((r) => setMsgs(r.data)).catch(() => {});
  useEffect(() => {
    load().then(() => api.post(`/patients/${patient.id}/team-messages/read`).catch(() => {}));
  }, [patient.id]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Card className="border-gray-200 mt-8" data-testid="team-messages-card">
      <CardContent className="p-6">
        <h3 className="font-semibold text-gray-800 mb-1 flex items-center gap-2"><MessageSquare className="w-4 h-4 text-sky-600" /> Messages de la famille</h3>
        <p className="text-sm text-gray-500 mb-4">Mots écrits ou vocaux envoyés par les proches de {patient.name}. Vous pouvez répondre si vous le souhaitez.</p>
        {msgs.length === 0 ? (
          <p className="text-gray-400 py-6 text-center" data-testid="team-messages-empty">Aucun message pour le moment.</p>
        ) : (
          <div className="space-y-3">
            {msgs.map((m) => (
              <div key={m.id} className={`rounded-xl border p-4 ${m.read ? "border-gray-200" : "border-sky-300 bg-sky-50/50"}`} data-testid={`team-msg-row-${m.id}`}>
                <p className="text-xs text-gray-500 mb-1">{m.family_name} · {when(m.created_at)}{!m.read && <span className="ml-2 text-sky-700 font-semibold">Nouveau</span>}</p>
                {m.text && <p className="text-gray-800 whitespace-pre-line">{m.text}</p>}
                {m.audio_url && <audio controls preload="none" src={`${BACKEND}${m.audio_url}`} className="w-full h-10 mt-2" />}
                {m.reply ? (
                  <p className="mt-3 text-sm text-emerald-800 bg-emerald-50 rounded-lg p-3" data-testid={`team-msg-replied-${m.id}`}>Votre réponse : {m.reply.text}</p>
                ) : <ReplyBox patientId={patient.id} m={m} onDone={load} />}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
